// src/components/Navigation/Sidebar.tsx
import React from 'react';
import { useProcessing } from '../../context/ProcessingContext';
import type { ProcessingState } from '../../types';

// Update StepId to match our workflow
export type StepId = 'upload' | 'grid' | 'interpolate' | 'contour' | 'map' | 'export';

interface Step {
  id: StepId;
  label: string;
  description: string;
  icon?: string;
  completionCriteria: (state: ProcessingState) => boolean;
  requiresGeographic?: boolean;
}

// Update workflow steps with geographic requirements
const WORKFLOW_STEPS: Step[] = [
  {
    id: 'upload',
    label: 'Upload',
    description: 'Upload and validate your data file',
    icon: '📤',
    completionCriteria: (state) => Boolean(state.rawData)
  },
  {
    id: 'grid',
    label: 'Grid',
    description: 'Configure grid resolution and settings',
    icon: '#',
    completionCriteria: (state) => Boolean(state.griddedData)
  },
  {
    id: 'interpolate',
    label: 'Interpolate',
    description: 'Apply interpolation methods',
    icon: '📊',
    completionCriteria: (state) => Boolean(state.griddedData)
  },
  {
    id: 'contour',
    label: 'Contour',
    description: 'Visualize interpolation results',
    icon: '📈',
    completionCriteria: (state) => Boolean(state.interpolatedData)
  },
  {
    id: 'map',
    label: 'Interact',
    description: 'View results on interactive map',
    icon: '🗺️',
    requiresGeographic: true,
    completionCriteria: (state) => Boolean(state.interpolatedData) && 
                                 Boolean(state.contourGeoJSON) &&
                                 Boolean(state.rawData?.geographic_metadata?.original_crs)
  },
  {
    id: 'export',
    label: 'Export',
    description: 'Export processed results',
    icon: '💾',
    completionCriteria: (state) => Boolean(state.interpolatedData)
  }
];

interface NavStepProps {
  step: Step;
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
  isProcessing: boolean;
  onClick: () => void;
}

const NavStep: React.FC<NavStepProps> = React.memo(({
  step,
  isActive,
  isCompleted,
  isDisabled,
  isProcessing,
  onClick
}) => {
  const stepNumber = WORKFLOW_STEPS.findIndex(s => s.id === step.id) + 1;
  
  return (
    <button
      className={`nav-step ${isActive ? 'active' : ''} 
                 ${isCompleted ? 'completed' : ''} 
                 ${isDisabled ? 'disabled' : ''}
                 ${isProcessing ? 'processing' : ''}`}
      onClick={onClick}
      disabled={isDisabled}
      aria-current={isActive ? 'step' : undefined}
      aria-label={`${step.label} - Step ${stepNumber} of ${WORKFLOW_STEPS.length}`}
      title={step.description}
    >
      <div className="step-content">
        <span className="step-indicator">
          {isProcessing ? (
            <span className="processing-spinner" />
          ) : step.icon && !isCompleted ? (
            <span className="step-icon">{step.icon}</span>
          ) : isCompleted ? (
            <span className="completion-icon">✓</span>
          ) : (
            <span className="step-number">{stepNumber}</span>
          )}
        </span>
        <span className="step-label">{step.label}</span>
      </div>
      {isProcessing && (
        <span className="visually-hidden">(Processing)</span>
      )}
    </button>
  );
});

NavStep.displayName = 'NavStep';

export const Sidebar: React.FC = () => {
  const { state, dispatch } = useProcessing();

  const handleStepNavigation = React.useCallback(
    (stepId: StepId) => {
      const targetStep = WORKFLOW_STEPS.find(step => step.id === stepId);
      const targetIndex = WORKFLOW_STEPS.findIndex(step => step.id === stepId);
      const currentIndex = WORKFLOW_STEPS.findIndex(
        step => step.id === state.currentViewStep
      );

      // Allow backwards navigation without restrictions
      if (targetIndex <= currentIndex) {
        dispatch({ type: 'SET_VIEW_STEP', payload: stepId });
        return;
      }

      // Check geographic data requirement for map step
      if (targetStep?.requiresGeographic && 
          (!state.rawData?.geographic_metadata?.original_crs || !state.contourGeoJSON)) {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: 'Interactive mapping requires geographic coordinates and contour data.' 
        });
        return;
      }

      // For forward navigation, check completion criteria
      const previousStepsCompleted = WORKFLOW_STEPS
        .slice(0, targetIndex)
        .every(step => step.completionCriteria(state));

      if (previousStepsCompleted) {
        dispatch({ type: 'SET_VIEW_STEP', payload: stepId });
      }
    },
    [state, dispatch]
  );

  const getStepStatus = React.useCallback(
    (step: Step, index: number) => {
      const isCompleted = step.completionCriteria(state);
      const isDisabled = !isCompleted && index > WORKFLOW_STEPS.findIndex(
        s => s.id === state.currentViewStep
      ) + 1;

      // Add additional check for map step availability
      if (step.id === 'map' && step.requiresGeographic) {
        const hasGeographicData = Boolean(state.rawData?.geographic_metadata?.original_crs);
        const hasContourData = Boolean(state.contourGeoJSON);
        if (!hasGeographicData || !hasContourData) {
          return {
            isCompleted: false,
            isDisabled: true,
            isActive: step.id === state.currentViewStep,
            isProcessing: state.isProcessing && step.id === state.currentViewStep
          };
        }
      }

      return {
        isCompleted,
        isDisabled,
        isActive: step.id === state.currentViewStep,
        isProcessing: state.isProcessing && step.id === state.currentViewStep
      };
    },
    [state]
  );

  return (
    <nav 
      className="nav-sidebar"
      role="navigation"
      aria-label="Processing Steps"
    >
      <div className="nav-steps">
        {WORKFLOW_STEPS.map((step, index) => {
          const status = getStepStatus(step, index);

          return (
            <NavStep
              key={step.id}
              step={step}
              isActive={status.isActive}
              isCompleted={status.isCompleted}
              isDisabled={status.isDisabled}
              isProcessing={status.isProcessing}
              onClick={() => handleStepNavigation(step.id)}
            />
          );
        })}
      </div>

      {state.error && (
        <div className="nav-error" role="alert">
          {state.error}
        </div>
      )}
    </nav>
  );
};

export default Sidebar;